import {
    INT_RANGE_REGEX,
    IntegerSchemaValues,
    MAX_INT_RANGE,
    POSITIVE_INT_REGEX,
    SPACE_AROUND_DASH_REGEX,
} from '../../../components/models/Integer/consts'

const parseInputToRange = (val: any): bigint[] => {
    if (typeof val !== 'string') return []

    const normalized = val.trim().replace(SPACE_AROUND_DASH_REGEX, '-')

    if (POSITIVE_INT_REGEX.test(normalized)) {
        try {
            return [BigInt(normalized)]
        } catch {
            return []
        }
    }

    if (INT_RANGE_REGEX.test(normalized)) {
        const [startStr, endStr] = normalized.split('-')
        const start = BigInt(startStr)
        const end = BigInt(endStr)

        if (start >= end) return []

        if (end - start >= BigInt(MAX_INT_RANGE)) return []

        const range: bigint[] = []
        for (let i = start; i <= end; i++) {
            range.push(i)
        }
        return range
    }

    return []
}

export const getIntegerRangeSchema = (inputString: any, schema: any) => {
    const baseTest = (
        name: string,
        message: string,
        testFn: (n: any) => boolean
    ) =>
        inputString.test(name, message, (val: any) => {
            const values = parseInputToRange(val)
            if (!values.length) return false
            if (!schema.is_required && (val === 'null' || val === null))
                return true
            return values.every(testFn)
        })

    if (typeof schema.greater_or_equal === 'string') {
        inputString = baseTest(
            IntegerSchemaValues.GREATER_OR_EQUAL,
            `Each number must be ≥ ${schema.greater_or_equal}`,
            (n) => {
                try {
                    return n >= BigInt(schema.greater_or_equal)
                } catch {
                    return false
                }
            }
        )
    }

    if (typeof schema.greater_than === 'string') {
        inputString = baseTest(
            IntegerSchemaValues.GREATER_THAN,
            `Each number must be > ${schema.greater_than}`,
            (n) => {
                try {
                    return n > BigInt(schema.greater_than)
                } catch {
                    return false
                }
            }
        )
    }

    if (typeof schema.less_or_equal === 'string') {
        inputString = baseTest(
            IntegerSchemaValues.LESS_OR_EQUAL,
            `Each number must be ≤ ${schema.less_or_equal}`,
            (n) => {
                try {
                    return n <= BigInt(schema.less_or_equal)
                } catch {
                    return false
                }
            }
        )
    }

    if (typeof schema.less_than === 'string') {
        inputString = baseTest(
            IntegerSchemaValues.LESS_THAN,
            `Each number must be < ${schema.less_than}`,
            (n) => {
                try {
                    return n < BigInt(schema.less_than)
                } catch {
                    return false
                }
            }
        )
    }

    if (schema.is_required) {
        inputString = inputString
            .required()
            .test('valid-format', 'Must be an integer or range', (val: any) => {
                return parseInputToRange(val).length > 0
            })
            .test(
                'max-range-size',
                `Range must not exceed ${MAX_INT_RANGE} elements`,
                (val: any) => {
                    return parseInputToRange(val).length <= MAX_INT_RANGE
                }
            )
    } else {
        inputString = inputString
            .transform((_: any, val: any) => (val === '' ? null : val))
            .nullable()
            .notRequired()
            .test('valid-format', 'Must be an integer or range', (val: any) => {
                return val === null || parseInputToRange(val).length > 0
            })
    }

    return inputString
}
